/**
 * Deduction logic, kept separate from the schema so the same code validates a
 * puzzle, builds one, and runs the renderer's elimination step.
 *
 * A puzzle is solvable when applying every clue leaves exactly one candidate,
 * and that candidate is the declared solution. The schema enforces it, so an
 * unsolvable puzzle can never reach the child.
 */

export interface DeductionCandidateLike {
  id: string
  label: string
  attributes: Record<string, string>
}

export interface DeductionClueLike {
  attribute: string
  value: string
  negate: boolean
}

export interface DeductionClue extends DeductionClueLike {
  id: string
  text: string
}

/** Does this candidate survive this clue? */
export function clueKeeps(clue: DeductionClueLike, candidate: DeductionCandidateLike): boolean {
  const actual = candidate.attributes[clue.attribute]
  return clue.negate ? actual !== clue.value : actual === clue.value
}

/** Candidates still standing after every clue is applied. */
export function applyClues<C extends DeductionCandidateLike>(
  candidates: readonly C[],
  clues: readonly DeductionClueLike[],
): C[] {
  return candidates.filter((candidate) => clues.every((clue) => clueKeeps(clue, candidate)))
}

/** How many candidates a clue removes from the given set. A clue that removes none is noise. */
export function clueEliminationCount(
  clue: DeductionClueLike,
  candidates: readonly DeductionCandidateLike[],
): number {
  return candidates.filter((candidate) => !clueKeeps(clue, candidate)).length
}

const PHRASING: Record<string, (value: string, negate: boolean) => string> = {
  color: (v, n) => (n ? `It is not ${v}.` : `It is ${v}.`),
  size: (v, n) => (n ? `It is not ${v}.` : `It is ${v}.`),
  group: (v, n) => (n ? `It is not a ${v}.` : `It is a ${v}.`),
  home: (v, n) => (n ? `It does not live in the ${v}.` : `It lives in the ${v}.`),
  food: (v, n) => (n ? `It does not eat ${v}.` : `It eats ${v}.`),
  sound: (v, n) => (n ? `It does not say ${v}.` : `It says ${v}.`),
  legs: (v, n) => (n ? `It does not have ${v} legs.` : `It has ${v} legs.`),
  covering: (v, n) => (n ? `It is not covered in ${v}.` : `It is covered in ${v}.`),
  moves: (v, n) => (n ? `It does not ${v}.` : `It can ${v}.`),
}

/** Child-facing clue wording. Plain, concrete, readable aloud. */
export function clueText(attribute: string, value: string, negate: boolean): string {
  const phrase = PHRASING[attribute]
  if (phrase) return phrase(value, negate)
  return negate ? `Its ${attribute} is not ${value}.` : `Its ${attribute} is ${value}.`
}

/**
 * Build a clue set that narrows `candidates` to exactly `solutionId`.
 *
 * Positive clues first (they read most naturally), then negative clues to peel
 * off whatever is left. Every emitted clue removes at least one candidate, so
 * the puzzle never contains a clue that teaches nothing.
 *
 * `attributeOrder` is a preference, not a restriction: a short list is how you
 * ask for a harder puzzle, because the attributes it leaves out have to be
 * settled by negation instead. Negative clues fall back to every attribute the
 * solution declares, so a short list narrows rather than deadlocks.
 *
 * Throws when the solution is not distinguishable — that is a bug in the
 * candidate set, not something to paper over.
 */
export function buildDeductionPuzzle(
  candidates: readonly DeductionCandidateLike[],
  solutionId: string,
  attributeOrder?: readonly string[],
): DeductionClue[] {
  const solution = candidates.find((candidate) => candidate.id === solutionId)
  if (!solution) throw new Error(`buildDeductionPuzzle: no candidate with id "${solutionId}"`)

  const attributes = attributeOrder ?? Object.keys(solution.attributes)
  const fallback = [...new Set([...attributes, ...Object.keys(solution.attributes)])]
  const clues: DeductionClue[] = []
  let remaining = [...candidates]

  const push = (attribute: string, value: string, negate: boolean): void => {
    clues.push({
      id: `clue-${clues.length + 1}`,
      attribute,
      value,
      negate,
      text: clueText(attribute, value, negate),
    })
    remaining = applyClues(remaining, [{ attribute, value, negate }])
  }

  for (const attribute of attributes) {
    if (remaining.length === 1) break
    const value = solution.attributes[attribute]
    if (value === undefined) continue
    if (clueEliminationCount({ attribute, value, negate: false }, remaining) > 0) {
      push(attribute, value, false)
    }
  }

  let guard = candidates.length + fallback.length
  while (remaining.length > 1 && guard-- > 0) {
    const other = remaining.find((candidate) => candidate.id !== solutionId)
    if (!other) break
    const attribute = fallback.find(
      (key) => other.attributes[key] !== undefined && other.attributes[key] !== solution.attributes[key],
    )
    if (!attribute) {
      throw new Error(
        `buildDeductionPuzzle: "${other.label}" is indistinguishable from "${solution.label}"`,
      )
    }
    push(attribute, other.attributes[attribute], true)
  }

  if (remaining.length !== 1 || remaining[0].id !== solutionId) {
    throw new Error('buildDeductionPuzzle: could not narrow the candidates to one')
  }
  return clues
}
